import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';

import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNode';

/**
 * A grid node's role in the upstream->active draw affordance: `source` for nodes in the layer
 * immediately upstream of the active method, `target` for nodes in the active layer, `null` for
 * everything else (and for every node when there is no active layer).
 */
export type ConnectionRole = 'source' | 'target' | null;

/** Minimal directed-edge shape (endpoints by node id). Live ReactFlow `Edge`s satisfy this too. */
interface EdgeEndpoints {
  source: string;
  target: string;
}

/**
 * Derives connection data for the upstream->active draw affordance. The role map is static (depends
 * only on the methods and the active layer). The source-handle visibility map additionally depends
 * on the live edges. Both maps are empty when there is no active layer.
 */
export function getNodeConnectionRoles(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>,
  edges: Array<EdgeEndpoints>,
  activeMethodId: string | null
): {
  roleByNodeId: ReadonlyMap<string, ConnectionRole>;
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>;
} {
  const {roleByNodeId, activeNodeIds, sourceNodeIds} = buildRoleByNodeId(conditions, methods, activeMethodId);
  const showPersistentSourceHandleByNodeId = buildShowPersistentSourceHandleByNodeId(
    edges,
    activeNodeIds,
    sourceNodeIds
  );
  return {roleByNodeId, showPersistentSourceHandleByNodeId};
}

/**
 * Assigns each grid node's connection role: active-layer nodes are `target`, the single
 * immediately-upstream layer's nodes are `source`, all others `null`. Also returns the active/source
 * id sets the edge-dependent pass needs. Empty when there is no active layer.
 */
function buildRoleByNodeId(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>,
  activeMethodId: string | null
): {roleByNodeId: Map<string, ConnectionRole>; activeNodeIds: Set<string>; sourceNodeIds: Set<string>} {
  const roleByNodeId = new Map<string, ConnectionRole>();
  const activeNodeIds = new Set<string>();
  const sourceNodeIds = new Set<string>();
  const activeIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  if (activeIndex < 0) {
    return {roleByNodeId, activeNodeIds, sourceNodeIds};
  }
  // The single immediately-preceding column, undefined when the active layer is the first column.
  const upstreamMethodId = activeIndex > 0 ? methods[activeIndex - 1].id : undefined;
  conditions.forEach((condition, conditionIndex) => {
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      methods.forEach((method) => {
        const nodeId = getProcedureRunDesignFlowchartNodeId({
          conditionIndex,
          replicateIndex,
          methodId: method.id,
        });
        const role: ConnectionRole =
          method.id === activeMethodId ? 'target' : method.id === upstreamMethodId ? 'source' : null;
        roleByNodeId.set(nodeId, role);
        if (role === 'target') {
          activeNodeIds.add(nodeId);
        } else if (role === 'source') {
          sourceNodeIds.add(nodeId);
        }
      });
    }
  });
  return {roleByNodeId, activeNodeIds, sourceNodeIds};
}

/**
 * Edge-dependent visibility of each upstream source handle: while any active-layer node still lacks
 * an incoming upstream->active edge, every source handle shows. Once every active node has one, only
 * sources without an outgoing upstream->active edge keep showing. Edges other than upstream->active
 * are ignored.
 */
function buildShowPersistentSourceHandleByNodeId(
  edges: Array<EdgeEndpoints>,
  activeNodeIds: Set<string>,
  sourceNodeIds: Set<string>
): Map<string, boolean> {
  const relevant = edges.filter((edge) => sourceNodeIds.has(edge.source) && activeNodeIds.has(edge.target));
  const incoming = new Set(relevant.map((edge) => edge.target));
  const outgoing = new Set(relevant.map((edge) => edge.source));
  const anyActiveLacksIncoming = activeNodeIds.size > 0 && [...activeNodeIds].some((id) => !incoming.has(id));
  return new Map([...sourceNodeIds].map((id) => [id, anyActiveLacksIncoming || !outgoing.has(id)]));
}
